<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'
import { FileText, Search, SlidersHorizontal, X } from 'lucide-vue-next'
import { computed, onUnmounted, ref, watch } from 'vue'

import AppDataTable from '@/components/AppDataTable.vue'
import type { TableColumn } from '@/components/AppDataTable.vue'
import AppDatePicker from '@/components/AppDatePicker.vue'
import AppPagination from '@/components/AppPagination.vue'
import PortalPartnerCombobox from '@/components/PortalPartnerCombobox.vue'
import RelativeTime from '@/components/RelativeTime.vue'
import StatusBadge from '@/components/applications/StatusBadge.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import PortalLayout from '@/layouts/PortalLayout.vue'
import { APPLICATION_STATUSES } from '@/lib/constants'
import { formatAmount, formatLabel } from '@/lib/utils'
import { portalOrganizationApplicationPath, portalOrganizationApplicationsPath } from '@/routes'

interface Application {
  id: string
  status: string
  loan_type: string
  purpose: string
  amount: number
  partner_id: number
  partner_name: string
  customer_id: number
  customer_name: string
  customer_email: string
  created_at: string
}

interface SelectedPartner {
  id: number
  name: string
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  q?: string | null
  status?: string | null
  partner_id?: string | null
  date_from?: string | null
  date_to?: string | null
}

const props = defineProps<{
  applications: Application[]
  selected_partner: SelectedPartner | null
  pagination: PaginationMeta
  filters: Filters
}>()

const ALL = '__all__'
const today = new Date().toISOString().split('T')[0]

const search = ref(props.filters.q ?? '')
const statusFilter = ref(props.filters.status ?? ALL)
const partnerFilter = ref(props.filters.partner_id ?? '')
const partnerName = ref(props.selected_partner?.name ?? '')
const dateFrom = ref(props.filters.date_from ?? '')
const dateTo = ref(props.filters.date_to ?? '')

const hasActiveFilters = computed(
  () => search.value !== '' || statusFilter.value !== ALL || partnerFilter.value !== ''
    || dateFrom.value !== '' || dateTo.value !== '',
)

const filterParams = computed(() => ({
  q:          search.value || undefined,
  status:     statusFilter.value !== ALL ? statusFilter.value : undefined,
  partner_id: partnerFilter.value || undefined,
  date_from:  dateFrom.value || undefined,
  date_to:    dateTo.value || undefined,
}))

function applyFilters() {
  const params = Object.fromEntries(Object.entries(filterParams.value).filter(([, v]) => v != null))
  router.get(portalOrganizationApplicationsPath(), params, { preserveState: true, replace: true })
}

let searchTimer: ReturnType<typeof setTimeout>
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
})
watch([statusFilter, partnerFilter, dateFrom, dateTo], applyFilters)
onUnmounted(() => clearTimeout(searchTimer))

function clearFilters() {
  search.value = ''
  statusFilter.value = ALL
  partnerFilter.value = ''
  partnerName.value = ''
  dateFrom.value = ''
  dateTo.value = ''
}

const tableColumns: TableColumn[] = [
  { key: 'customer', label: 'Customer' },
  { key: 'partner', label: 'Partner' },
  { key: 'purpose', label: 'Purpose' },
  { key: 'amount', label: 'Amount' },
  { key: 'status', label: 'Status' },
  { key: 'date', label: 'Date' },
  { key: 'link', label: '', align: 'right' },
]
</script>

<template>
  <Head title="Applications" />
  <PortalLayout actor-type="organization">
    <Card>
      <CardHeader class="pb-3">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <FileText class="text-muted-foreground size-5" />
            <div>
              <CardTitle class="text-base">Applications</CardTitle>
              <CardDescription>
                {{ pagination.total_count }} application{{ pagination.total_count !== 1 ? 's' : '' }}
                {{ hasActiveFilters ? 'found' : 'total' }}
              </CardDescription>
            </div>
          </div>
          <SlidersHorizontal class="text-muted-foreground size-4" />
        </div>
      </CardHeader>

      <!-- Filters -->
      <div class="border-border border-b px-4 pb-4">
        <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div class="relative min-w-48 flex-1">
            <Search class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
            <Input v-model="search" placeholder="Search by customer name or email…" class="pl-9" />
          </div>

          <Select v-model="statusFilter">
            <SelectTrigger class="w-full sm:w-40">
              <SelectValue placeholder="Any status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem :value="ALL">Any status</SelectItem>
              <SelectItem v-for="s in APPLICATION_STATUSES" :key="s" :value="s">{{ formatLabel(s) }}</SelectItem>
            </SelectContent>
          </Select>

          <div class="w-full sm:w-44">
            <PortalPartnerCombobox
              v-model="partnerFilter"
              v-model:partner-name="partnerName"
              clearable
              placeholder="Any partner"
            />
          </div>

          <div class="flex w-full flex-col items-center gap-1.5 sm:flex-row md:w-90">
            <AppDatePicker v-model="dateFrom" :max="dateTo || today" placeholder="From" class="w-full sm:w-36" />
            <span class="text-muted-foreground hidden text-xs select-none sm:block">–</span>
            <AppDatePicker v-model="dateTo" :min="dateFrom || undefined" :max="today" placeholder="To" class="w-full sm:w-36" />
          </div>

          <Button
            v-if="hasActiveFilters"
            variant="ghost"
            size="sm"
            class="text-muted-foreground hover:text-foreground w-full shrink-0 sm:w-auto"
            @click="clearFilters"
          >
            <X class="size-4" />
            Clear
          </Button>
        </div>
      </div>

      <CardContent class="p-0">
        <div v-if="applications.length === 0" class="flex flex-col items-center gap-3 py-16 text-center">
          <FileText class="text-muted-foreground/40 size-10" />
          <p class="text-muted-foreground text-sm">
            {{ hasActiveFilters ? 'No applications match your filters' : 'No applications yet' }}
          </p>
          <Button v-if="hasActiveFilters" variant="outline" size="sm" @click="clearFilters">
            <X class="size-4" />
            Clear filters
          </Button>
        </div>
        <template v-else>
          <AppDataTable :columns="tableColumns">
            <tr v-for="app in applications" :key="app.id" class="border-border hover:bg-muted/50 border-b last:border-0 transition-colors">
              <td class="px-6 py-4">
                <Link :href="portalOrganizationApplicationPath(app.id)" class="font-medium hover:underline">{{ app.customer_name }}</Link>
                <p class="text-muted-foreground mt-0.5 text-xs">{{ app.customer_email }}</p>
              </td>
              <td class="text-muted-foreground px-6 py-4">{{ app.partner_name }}</td>
              <td class="px-6 py-4">{{ formatLabel(app.purpose) }}</td>
              <td class="text-muted-foreground px-6 py-4">{{ formatAmount(app.amount) }}</td>
              <td class="px-6 py-4"><StatusBadge :status="app.status" /></td>
              <td class="text-muted-foreground px-6 py-4"><RelativeTime :date="app.created_at" /></td>
              <td class="px-6 py-4 text-right whitespace-nowrap">
                <Link :href="portalOrganizationApplicationPath(app.id)" class="text-primary text-xs hover:underline">View →</Link>
              </td>
            </tr>
          </AppDataTable>
          <AppPagination
            v-if="pagination.total_pages > 1"
            :current-page="pagination.current_page"
            :total-pages="pagination.total_pages"
            :total-count="pagination.total_count"
            :per-page="pagination.per_page"
            :base-url="portalOrganizationApplicationsPath()"
            :extra-params="filterParams"
            class="px-4"
          />
        </template>
      </CardContent>
    </Card>
  </PortalLayout>
</template>
